import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { OTPInput, ResendCountdown } from '@/components'
import { BaseView } from '@/components/base'
import {
  useCheckRecoveryCodeSentToEmail,
  useCheckRecoveryCodeSentToPhone,
  useSendPhoneVerificationCode,
  useSendRecoveryCodeToEmail,
  useSendRecoveryCodeToPhone,
  VerifyCodeDTO,
} from '@/domain'
import { useAuthScopedStore } from '@/store/scoped'
import { cleanNumberAndHandleDDIBeforeSendToAPI } from '@/utils'
import { MessagingMethodCode } from '@/domain/messagingMethods'

const CELL_COUNT = 4
const defaultCodes = Array(CELL_COUNT).fill('')

export const ForgotPasswordEnterVerificationCodeForm = () => {
  const [codes, setCodes] = useState<string[]>(defaultCodes)
  const [inputErrorMessage, setInputErrorMessage] = useState('')

  const { t } = useTranslation()
  const navigate = useNavigate()

  const email = useAuthScopedStore((state) => state.recoverPasswordForm?.email)
  const phone = useAuthScopedStore((state) => state.recoverPasswordForm?.phone)

  const messagingMethod = useAuthScopedStore((state) => state.messagingMethod)
  const userId = useAuthScopedStore(
    (state) => state.recoverPasswordForm?.userId,
  )
  const identificationMethod = useAuthScopedStore(
    (state) => state.identificationMethod,
  )
  // console.log('identificationMethod', identificationMethod)
  const recoveryMethod = useAuthScopedStore(
    (state) => state.recoverPasswordForm?.recoveryMethod,
  )
  // console.log('recoveryMethod', recoveryMethod)
  const setRecoverPasswordForm = useAuthScopedStore(
    (state) => state.setRecoverPasswordForm,
  )

  const isEmailIdentificationMethod =
    identificationMethod === 'email' || recoveryMethod === 'email'

  const cleanPhoneNumber = cleanNumberAndHandleDDIBeforeSendToAPI(phone || '')

  const onSuccess = useCallback(
    async (data: VerifyCodeDTO) => {
      const { verify_token } = data

      // Save verify_token for next steps
      if (verify_token) {
        setRecoverPasswordForm({
          userId: userId || '',
          verifyToken: verify_token,
        })
      }

      // Navigate to create new password screen
      navigate('/auth/forgot-password/create-new-password')
    },
    [navigate, setRecoverPasswordForm, userId],
  )

  const onError = useCallback(
    ({ code, errorMessage }: { code?: string; errorMessage?: string }) => {
      if (!errorMessage) return

      if (code === 'TOO_MANY_ATTEMPTS') {
        setCodes(defaultCodes)
        toast.error(errorMessage)
        return
      }

      if (code === 'CODE_INVALID') {
        setInputErrorMessage(t('api:validationErrors.invalidCode'))
        setCodes(defaultCodes)
        return
      }

      toast.error(errorMessage)
    },
    [t],
  )

  const {
    mutateAsync: checkRecoveryCodeSentToEmail,
    isPending: isCheckingCodeSentToEmail,
  } = useCheckRecoveryCodeSentToEmail({
    onSuccess,
    onError,
  })

  const {
    mutateAsync: checkRecoveryCodeSentToPhone,
    isPending: isCheckingCodeSentToPhone,
  } = useCheckRecoveryCodeSentToPhone({
    onSuccess,
    onError,
  })
  const {
    mutateAsync: resendVerificationCodeToPhone,
    isPending: isSendingCodeToPhone,
  } = useSendRecoveryCodeToPhone({
    onError: ({ errorMessage }) => {
      if (errorMessage) {
        toast.error(errorMessage)
      }
    },
  })
  const {
    mutateAsync: resendVerificationCodeToEmail,
    isPending: isSendingCodeToEmail,
  } = useSendRecoveryCodeToEmail({
    onError: ({ errorMessage }) => {
      if (errorMessage) {
        toast.error(errorMessage)
      }
    },
  })

  const handleCodeVerification = useCallback(
    async (fullCode: string) => {
      const isLoading = isCheckingCodeSentToEmail || isCheckingCodeSentToPhone
      if (isLoading) return

      setInputErrorMessage('') // Clear errors

      if (isEmailIdentificationMethod) {
        await checkRecoveryCodeSentToEmail({
          user_id: userId || '',
          email: email || '',
          code: fullCode,
        })
      } else {
        // Phone verification - phone is stored as string in recoverPasswordForm
        await checkRecoveryCodeSentToPhone({
          user_id: userId || '',
          phone_number: phone || '',
          code: fullCode,
        })
      }
    },
    [
      isCheckingCodeSentToEmail,
      isCheckingCodeSentToPhone,
      isEmailIdentificationMethod,
      checkRecoveryCodeSentToEmail,
      checkRecoveryCodeSentToPhone,
      userId,
      email,
      phone,
    ],
  )
  const handleResendCode = useCallback(() => {
    if (!cleanPhoneNumber || isSendingCodeToPhone || isSendingCodeToEmail)
      return

    if (isEmailIdentificationMethod) {
      resendVerificationCodeToEmail({
        email: email || '',
      })
    } else {
      resendVerificationCodeToPhone({
        phone_number: cleanPhoneNumber,
        send_method: messagingMethod as MessagingMethodCode,
      })
    }
  }, [
    cleanPhoneNumber,
    messagingMethod,
    resendVerificationCodeToPhone,
    isSendingCodeToPhone,
    resendVerificationCodeToEmail,
    isSendingCodeToEmail,
    isEmailIdentificationMethod,
    email,
  ])
  return (
    <BaseView gap={36} center>
      <OTPInput
        autoFocus
        autoFocusAfterClean
        numberCells={4}
        cellStyle={{
          fontSize: 32,
          fontWeight: 600,
        }}
        keyboardType="numeric"
        textContentType="one-time-code"
        variant="filled"
        codes={codes}
        isSubmitting={isCheckingCodeSentToEmail || isCheckingCodeSentToPhone}
        onSubmit={handleCodeVerification}
        setCodes={setCodes}
        errorMessage={inputErrorMessage}
        setErrorMessage={setInputErrorMessage}
      />
      <BaseView gap={28} center>
        {/* Messaging method icon/image could be added here */}
        <ResendCountdown onPress={handleResendCode} />
      </BaseView>
    </BaseView>
  )
}
